import { useState, useEffect, useContext } from 'react';
import styles from './PortalTabs.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { useNavigate } from 'react-router';
import {
    queryPortalPages,
    updatePortalTabsOrder,
} from '../../services/portalPageService';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import { getText } from '../../i18n';

const PortalTabs = () => {
    const [portalTabs, setPortalTabs] = useState([]);
    const [originalTabs, setOriginalTabs] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const { showModal } = useContext(ModalContext);

    const fetchPortalTabs = async () => {
        setIsLoading(true);
        try {
            const { viewModels } = await queryPortalPages(false);
            console.log(viewModels);

            const mappedTabs = viewModels.map(tab => ({
                id: tab.id,
                name: tab.name,
                status: tab.status,
                createdAt: tab.createdAt,
                order: tab.order,
            }));

            const sortedTabs = mappedTabs.sort((a, b) => {
                if (a.order !== null && b.order !== null) {
                    return a.order - b.order;
                } else if (a.order === null && b.order === null) {
                    return new Date(a.createdAt) - new Date(b.createdAt);
                } else if (a.order === null) {
                    return 1;
                } else {
                    return -1;
                }
            });

            setPortalTabs(sortedTabs);
            setOriginalTabs(sortedTabs);
        } catch (error) {
            console.error(error);
            Toast.error(getText('msg_d81a0125e7ac'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPortalTabs();
    }, []);

    const handleReset = () => {
        setPortalTabs(originalTabs);
    };

    const hasUnsavedChanges = () => {
        return JSON.stringify(portalTabs) !== JSON.stringify(originalTabs);
    };

    const handleClose = () => {
        if (hasUnsavedChanges()) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: () => {
                    navigate(`/portal-pages`);
                },
                onNo: () => {
                    return;
                },
                yesButtonText: getText('msg_5397e0583f14'),
                noButtonText: getText('msg_816c52fd2bdd'),
            });
        } else {
            navigate(`/portal-pages`);
        }
    };

    const renderTabs = () => {
        const items = [];

        // Add an empty item at the top
        items.push(
            <div
                key="empty-top"
                className={`${styles.emptyItem} ${hoveredIndex === -1 ? styles.hovered : ''}`}
                onDragOver={handleDragOver}
                onDrop={handleDropTop}
                onDragEnter={() => setHoveredIndex(-1)}
                onDragLeave={() => setHoveredIndex(null)}
            />,
        );

        // Add the portal tab items
        portalTabs.forEach((tab, index) => {
            items.push(
                <div
                    key={tab.id}
                    className={`${styles.portalTabItem} ${hoveredIndex === index ? styles.hovered : ''} ${tab.status !== 1 ? styles.inactive : ''} d-flex`}
                    draggable
                    data-index={index}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    onDragEnter={() => setHoveredIndex(index)}
                    onDragLeave={() => setHoveredIndex(null)}
                >
                    <p className={styles.tabName}>{tab.name}</p>
                    {tab.status !== 1 && (
                        <i
                            className={`${styles.eyeSlashIcon} bi bi-eye-slash-fill`}
                        ></i>
                    )}
                </div>,
            );

            // Add an empty item after each tab
            items.push(
                <div
                    key={`empty-${index}`}
                    className={`${styles.emptyItem} ${hoveredIndex === index ? styles.hovered : ''}`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnter={() => setHoveredIndex(index)}
                    onDragLeave={() => setHoveredIndex(null)}
                />,
            );
        });

        return items;
    };

    const handleDragStart = e => {
        const index = e.target.dataset.index;
        setDraggedIndex(index);
        e.dataTransfer.setData('text/plain', index);
        e.target.classList.add('dragging');
    };

    const handleDragOver = e => {
        e.preventDefault();
    };

    const handleDrop = e => {
        e.preventDefault();
        const targetIndex = hoveredIndex;

        let newIndex;
        if (draggedIndex < targetIndex) {
            newIndex = targetIndex - 1;
        } else {
            newIndex = targetIndex;
        }

        if (newIndex < 0) {
            newIndex = 0;
        }

        if (draggedIndex !== newIndex) {
            const updatedTabs = [...portalTabs];
            const draggedTab = updatedTabs[draggedIndex];
            updatedTabs.splice(draggedIndex, 1);
            updatedTabs.splice(newIndex + 1, 0, draggedTab);
            setPortalTabs(updatedTabs);
        }

        setHoveredIndex(null);
    };

    const handleDropTop = e => {
        e.preventDefault();
        const targetIndex = hoveredIndex;

        let newIndex = 0;

        if (draggedIndex !== newIndex) {
            const updatedTabs = [...portalTabs];
            const draggedTab = updatedTabs[draggedIndex];
            updatedTabs.splice(draggedIndex, 1);
            updatedTabs.splice(newIndex, 0, draggedTab);
            setPortalTabs(updatedTabs);
        }

        setHoveredIndex(null);
    };

    const handleDragEnd = e => {
        e.target.classList.remove('dragging');
        setHoveredIndex(null);
    };

    const handleSave = async () => {
        const portalOrders = portalTabs.map((tab, index) => ({
            portalId: tab.id,
            order: index,
        }));

        try {
            await updatePortalTabsOrder(portalOrders);
            setOriginalTabs(portalTabs);
            Toast.success(getText('msg_5e997b46f787'));
        } catch (error) {
            console.error('Error saving portal tabs order:', error);
            Toast.error(
                getText('msg_044a5d43aa93'),
            );
        }
    };

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="page-content" style={{ height: '100%' }}>
            <div className="card" style={{ height: '100%', overflow: 'auto' }}>
                <div className="card-body px-0 py-4 b">
                    <div className="d-flex justify-content-between align-items-center pb-1">
                        <p className="page-title float-start card-title ms-4 mb-0">
                            {getText('msg_7e41b9999d42')}
                        </p>
                        <div className="float-end d-flex align-items-center me-4">
                            <span
                                id="resetButton"
                                onClick={handleReset}
                                className={`ing-text-buttons mx-3 cursor-pointer ${!hasUnsavedChanges() ? styles.disabled : ''}`}
                                style={{
                                    pointerEvents: hasUnsavedChanges()
                                        ? 'auto'
                                        : 'none',
                                    opacity: hasUnsavedChanges() ? 1 : 0.5,
                                }}
                            >
                                <i className="bi bi-arrow-repeat me-2" />
                                {getText('msg_44c57abd888a')}
                            </span>
                            <span
                                id="saveButton"
                                onClick={handleSave}
                                className="ing-text-buttons mx-3 cursor-pointer"
                            >
                                <i className="bi bi-check-lg me-2" />
                                {getText('msg_efc007a393f6')}
                            </span>
                            <span
                                id="closeButton"
                                onClick={handleClose}
                                className="ing-text-buttons mx-3 cursor-pointer"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>
                    <p
                        className={`ms-4 ing-text-heading4 mb-5 ${styles.pageSubheader}`}
                    >
                        {getText('msg_d8d7178b40d0')}
                        <br />
                        {getText('msg_522e08950bf1')}
                    </p>
                    <div id="portalTabs" className={styles.portalTabsList}>
                        {renderTabs()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PortalTabs;
